import { FC, useMemo, useState } from 'react';

import { Trigger } from 'components/FilterComponents/Trigger/Trigger';
import { TriggerState } from 'components/FilterComponents/Trigger/types';
import MonthPicker from 'components/MonthPicker/MonthPicker';
import { formatMonthYearLong } from 'components/MonthPicker/MonthPicker.utils';
import { Popover } from 'components/Popover';

const DEFAULT_MONTH_FILTER_LABEL = 'Month';
const DEFAULT_MONTH_FILTER_TEST_ID = 'month-filter';
const MONTH_FILTER_POPOVER_WIDTH = 320;

interface IMonthFilterProps {
  selectedMonth: Date;
  onSelectedMonthChange: (nextSelectedMonth: Date) => void;
  minSelectableMonth?: Date;
  maxSelectableMonth?: Date;
  label?: string;
  dataTestId?: string;
}

export const MonthFilter: FC<IMonthFilterProps> = ({
  selectedMonth,
  onSelectedMonthChange,
  minSelectableMonth,
  maxSelectableMonth,
  label = DEFAULT_MONTH_FILTER_LABEL,
  dataTestId = DEFAULT_MONTH_FILTER_TEST_ID
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const labelParts = useMemo(() => {
    return [label, formatMonthYearLong(selectedMonth)];
  }, [label, selectedMonth]);

  const handleMonthSelected = (nextSelectedMonth: Date) => {
    onSelectedMonthChange(nextSelectedMonth);
    setIsOpen(false);
  };

  return (
    <div data-testid={dataTestId}>
      <Popover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        width={MONTH_FILTER_POPOVER_WIDTH}
        trigger={
          <Trigger
            label={label}
            labelParts={labelParts}
            state={TriggerState.FILLED}
            isActive={isOpen}
            hideIndicator
            dataTestId={`${dataTestId}-trigger`}
            onClick={() => setIsOpen(true)}
          />
        }
      >
        <MonthPicker
          dataTestId={`${dataTestId}-picker`}
          value={selectedMonth}
          minSelectableMonth={minSelectableMonth}
          maxSelectableMonth={maxSelectableMonth}
          onChange={handleMonthSelected}
        />
      </Popover>
    </div>
  );
};
